/**
 * "Brief me" (/dashboard/legislation/:recordId/brief; §2.9, screenshots/47-brief-me.png; feature briefMe).
 * Written server-side by Claude from public records (worker/src/brief.js). Every claim carries source chips
 * linking to the record, and unsourcedClaims() keeps any claim without a real source off the page.
 * Toss-up percentages come from forecastVote(), not the model. briefChanges() compares the brief's
 * inputs with the record now and shows what changed (a forecast moving, a new timeline step, a new document).
 */
import { useEffect, useRef, useState } from "react";
import { ArrowLeft, Download, RefreshCw, Sparkles, Users } from "lucide-react";
import { Face } from "../components/shared/Face";
import { List, ListRow, RowCell, RowLead, RowMain, RowSub, RowTitle } from "../components/shared/List";
import { toastStore } from "../stores/toastStore";
import { ForecastChip } from "../forecast/ForecastViews";
import { labelOf } from "../legislation/logic";
import { briefChanges, unsourcedClaims, type Brief } from "./brief";
import type { LegislationDetail } from "../legislation/types";
import { Crumbs, sectionCrumb } from "../app/crumbs";
import type { OrgRole } from "../nav/navConfig";
import { BriefLimitCard } from "../plans/LimitCards";
import { briefsReached, limitAnswer, type BriefAllowance } from "../plans/planLimits";

const when = (iso: string) => new Date(iso).toLocaleString("en-US", { timeZone: "America/Chicago", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

/** The brief's inputs fingerprint, computed from the record as it is now (same shape the Worker stores). */
export function inputsNow(b: Brief, d: LegislationDetail): Brief["inputs"] {
  const client = d.lobbying.clients.find((c) => !c.isTeam);
  const rows = client ? d.lobbying.influence[client.id] ?? [] : [];
  const mine = d.myPosition ?? client?.position;
  const forecast: Record<string, number> = {};
  for (const p of b.people) {
    const r = rows.find((x) => x.personId === p.personId);
    if (r && client) forecast[p.personId] = mine === client.position ? r.forecast.p : 1 - r.forecast.p;
  }
  return { forecast, timelineSteps: d.timeline.filter((e) => !e.upcoming).length, documents: d.documents.length };
}

export function BriefView({ detail, initial, role = null }: { detail: LegislationDetail; initial: Brief | null; role?: OrgRole | null }) {
  const [brief, setBrief] = useState<Brief | null>(initial);
  const [busy, setBusy] = useState(false);
  const [limit, setLimit] = useState<BriefAllowance | null>(null);   // the plan's briefs a month are used up (k9qobd #2)
  const base = `/api/dashboard/legislation/${encodeURIComponent(detail.recordId)}/brief`;
  const regenerate = async () => {
    setBusy(true);
    const r = await fetch(base, { method: "POST", credentials: "include" }).catch(() => null);
    const json = r ? await r.json().catch(() => null) : null;
    const body = r?.ok ? (json as { brief: Brief }) : null;
    setBusy(false);
    const reached = limitAnswer(json);
    if (body?.brief) setBrief(body.brief);
    else if (reached?.code === "limit_reached") setLimit(reached.briefs);
    else toastStore.getState().show({ message: "Couldn't write the brief. Try again." });
  };
  const started = useRef(false);
  useEffect(() => { if (!initial && !started.current) { started.current = true; void regenerate(); } }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const share = async () => {
    const r = await fetch(`${base}/share`, { method: "POST", credentials: "include" }).catch(() => null);
    toastStore.getState().show({ message: r?.ok ? "Shared with your team." : "Couldn't share the brief. Try again." });
  };
  const bad = new Set(brief ? unsourcedClaims(brief) : []);   // generator bug: never render these
  const src = new Map((brief?.sources ?? []).map((s) => [s.key, s]));
  const chips = (keys: string[]) => (
    <span className="lg-brief__chips">{keys.map((k) => src.get(k) && <a key={k} className="lg-src" href={src.get(k)!.href} title={src.get(k)!.label}>{k}</a>)}</span>
  );
  const changed = brief ? briefChanges(brief, inputsNow(brief, detail)) : [];
  const title = detail.shortTitle || detail.title;
  return (
    <div className="page lg-brief">
      <Crumbs trail={[sectionCrumb("legislation"), { label: `${detail.recordId} · ${title}`, href: `/dashboard/legislation/${encodeURIComponent(detail.recordId)}` }, { label: "Brief me" }]} />
      <header className="page-header">
        <div className="lg-title-block">
          <span className="ai-chip lg-brief__chip"><Sparkles size={11} aria-hidden />Brief{brief ? ` · generated ${when(brief.generatedAt)}` : ""}</span>
          <h1 className="page-title">Brief: {brief?.title || title}</h1>
          {brief?.audience && <p className="page-subtitle">{brief.audience}</p>}
        </div>
        <div className="actions">
          <button className="btn" disabled={busy} onClick={() => void regenerate()}><RefreshCw size={14} aria-hidden />Regenerate</button>
          <button className="btn" disabled={!brief} onClick={() => print()}><Download size={14} aria-hidden />Download PDF</button>
          <button className="btn btn--primary" disabled={!brief} onClick={() => void share()}><Users size={14} aria-hidden />Share with team</button>
        </div>
      </header>
      {briefsReached(limit) && <BriefLimitCard allowance={limit} role={role} onDismiss={() => {
        setLimit(null);
        if (!brief) location.assign(`/dashboard/legislation/${encodeURIComponent(detail.recordId)}`);
      }} />}
      <div className="cols">
        <article className="lg-brief__doc">
          {changed.length > 0 && (
            <div className="lg-brief__changed" role="status">
              <ul>{changed.map((c) => <li key={c}>{c}</li>)}</ul>
              <button className="btn btn--small lg-btn-sm" disabled={busy} onClick={() => void regenerate()}><RefreshCw size={14} aria-hidden />Regenerate</button>
            </div>
          )}
          {!brief ? <p className="lg-card__sub">Generating…</p> : (<>
            {brief.bottomLine && <div className="lg-brief__bottom"><span className="eyebrow lg-kicker">Bottom line</span><p>{brief.bottomLine}</p></div>}
            {brief.sections.filter((s) => s.title !== "The other side").map((s) => <Section key={s.title} title={s.title} claims={s.claims.filter((c) => !bad.has(c.text))} chips={chips} />)}
            {brief.people.filter((p) => !bad.has(p.note)).length > 0 && (
              <section className="lg-brief__sec"><h2>Toss-ups to talk to first</h2>
                <List className="lg-brief__toss">{brief.people.filter((p) => !bad.has(p.note)).map((p) => (
                  <ListRow key={p.personId} className="lg-brief__person">
                    <RowLead><Face name={p.name} photoUrl={p.photoUrl} size={40} /></RowLead>
                    <RowMain min={200}>
                      <span className="lg-brief__head"><RowTitle as="b">{p.name}</RowTitle><small>Ward {p.ward}</small>
                        <ForecastChip label={labelOf(p.forecastPct / 100)} perspective="your" text={`${p.forecastPct}% your way`} /></span>
                      <RowSub wrap className="lg-brief__note">{p.note}</RowSub>
                    </RowMain>
                    <RowCell>{chips(p.sources)}</RowCell>
                  </ListRow>
                ))}</List>
              </section>
            )}
            {brief.sections.filter((s) => s.title === "The other side").map((s) => <Section key={s.title} title={s.title} claims={s.claims.filter((c) => !bad.has(c.text))} chips={chips} />)}
            <p className="lg-brief__foot">AI-generated from public records. Check the sources before sharing outside your team.</p>
          </>)}
        </article>
        <aside className="cols__side">
          {brief && (
            <section className="card lg-brief__sources">
              <h3 className="lg-card__title lg-brief__srctitle">Sources · {brief.sources.length}</h3>
              {brief.sources.map((s) => <a key={s.key} href={s.href}><span className="lg-src">{s.key}</span>{s.label}</a>)}
            </section>
          )}
        </aside>
      </div>
    </div>
  );
}

function Section({ title, claims, chips }: { title: string; claims: { text: string; sources: string[] }[]; chips: (k: string[]) => React.ReactNode }) {
  if (!claims.length) return null;
  return (
    <section className="lg-brief__sec"><h2>{title}</h2>
      <ul>{claims.map((c) => <li key={c.text}><span>{c.text}</span>{chips(c.sources)}</li>)}</ul>
    </section>
  );
}
